import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useApi, errorMessage } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { UsersTable } from "./UsersTable";
import { ConfirmLimitDialog } from "./ConfirmLimitDialog";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border bg-card px-5 py-4" data-testid={testId}>
    <p className="eyebrow text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-3xl font-extrabold">{value}</p>
  </div>
);

export const AdminUsers = () => {
  const api = useApi();
  const { me, refresh } = useMe();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/users").then(setData).catch((e) => { toast.error(errorMessage(e)); setData({ roles: [], users: [] }); });
  }, [api]);

  const patchUser = (id, changes) => setData((d) => ({ ...d, users: d.users.map((u) => (u.user_id === id ? { ...u, ...changes } : u)) }));

  const setLimit = useCallback(async (user, limit, confirmIds = []) => {
    try {
      const r = await api.patch(`/users/${user.user_id}/limit`, { limit, confirm_ids: confirmIds });
      patchUser(user.user_id, { project_limit: r.project_limit, project_count: r.project_count });
      toast.success(`Limit set to ${r.project_limit}${r.deleted ? ` · ${r.deleted} project${r.deleted === 1 ? "" : "s"} deleted` : ""}`);
      if (user.user_id === me?.user_id) refresh();
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.to_delete) setPending({ user, limit, ...d });
      else toast.error(errorMessage(e));
      return false;
    }
  }, [api, me, refresh]);

  const setRole = async (user, role) => {
    try {
      await api.patch(`/users/${user.user_id}/role`, { role });
      patchUser(user.user_id, { role });
      toast.success(`${user.name || user.email} is now ${role === "admin" ? "an admin" : "a member"}`);
      if (user.user_id === me?.user_id) refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const users = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.users || []).filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q));
  }, [data, query]);

  const totalProjects = (data?.users || []).reduce((n, u) => n + u.project_count, 0);

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:py-14">
      <div className="fade-up">
        <p className="eyebrow text-accent dark:text-primary">Admin</p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">Users</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Everyone with a HubPilot account, and how many projects they've set up. New accounts can keep up to {data?.default_limit ?? 10} projects.</p>
      </div>
      <div className="fade-up delay-1 mt-8 grid grid-cols-2 gap-4 sm:max-w-md">
        <Stat label="Accounts" value={data ? data.users.length : "–"} testId="admin-stat-accounts" />
        <Stat label="Projects" value={data ? totalProjects : "–"} testId="admin-stat-projects" />
      </div>
      <div className="fade-up delay-2 mt-10">
        <div className="relative sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email" className="rounded-full pl-9" data-testid="admin-user-search-input" />
        </div>
        {data === null
          ? <Skeleton className="mt-4 h-64 rounded-2xl" />
          : <UsersTable users={users} roles={data.roles} meId={me?.user_id} onSetLimit={setLimit} onSetRole={setRole} />}
      </div>
      <ConfirmLimitDialog
        pending={pending}
        onCancel={() => setPending(null)}
        onConfirm={async () => { const p = pending; setPending(null); await setLimit(p.user, p.limit, p.ids); }}
      />
    </section>
  );
};
